/**
 * Real TOTP 2FA setup flow — replaces the iter25 placeholder toggle.
 *
 * Steps:
 *  1. If twofa_enabled=false: show "Enable" → call /auth/2fa/setup-start →
 *     display QR + secret + 6-digit input → call /auth/2fa/setup-verify →
 *     show recovery codes (one-shot).
 *  2. If twofa_enabled=true: show "On" + "Disable" → call /auth/2fa/full-disable
 *     with a fresh TOTP/recovery code.
 */
import { useEffect, useState } from "react";
import { Copy, KeyRound, Lock, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function TwoFASetup({ testid = "twofa-setup" }) {
  const [status, setStatus] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | enrolling | verifying | recovery | disabling
  const [enrollment, setEnrollment] = useState(null); // {secret, provisioning_uri, qr_png_base64}
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/auth/2fa/status");
      setStatus(r.data);
    } catch {
      setStatus({ enabled: false });
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup-start");
      setEnrollment(r.data);
      setPhase("verifying");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't start setup.");
    } finally {
      setBusy(false);
    }
  };

  const verifyEnroll = async () => {
    if (code.trim().length < 6) {
      toast("Please paste the 6-digit code.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup-verify", { code: code.trim() });
      setRecoveryCodes(r.data.recovery_codes || []);
      setPhase("recovery");
      setStatus({ enabled: true });
      toast("Two-factor sign-in is gently enabled.");
    } catch (err) {
      toast(err.response?.data?.detail || "Code didn't match.");
    } finally {
      setBusy(false);
    }
  };

  const fullDisable = async () => {
    if (code.trim().length < 6) {
      toast("Paste a code from your authenticator (or a recovery code).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/2fa/full-disable", { code: code.trim() });
      toast("Two-factor sign-in turned off.");
      setStatus({ enabled: false });
      setPhase("idle");
      setCode("");
    } catch (err) {
      toast(err.response?.data?.detail || "Code didn't match.");
    } finally {
      setBusy(false);
    }
  };

  const closeAll = () => {
    setPhase("idle");
    setEnrollment(null);
    setRecoveryCodes([]);
    setCode("");
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      toast("Recovery codes copied.");
    } catch {
      toast("Copy failed — please select & copy manually.");
    }
  };

  if (!status) return null;

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between gap-3">
        <span
          data-testid={`${testid}-status`}
          className={
            "text-sm font-medium " +
            (status.enabled ? "text-[#1A1B2E]" : "text-[#525252]")
          }
        >
          {status.enabled ? "On — six-digit code at sign-in" : "Off"}
        </span>
        <button
          type="button"
          onClick={() => {
            if (status.enabled) {
              setPhase("disabling");
            } else {
              startEnroll();
            }
          }}
          disabled={busy}
          data-testid={`${testid}-toggle`}
          className={
            "inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs tracking-[0.16em] uppercase font-medium transition-colors " +
            (status.enabled
              ? "bg-[#1F3A2A]/10 text-[#1F3A2A] hover:bg-[#1F3A2A]/20"
              : "bg-[#132619] text-white hover:bg-[#0b2117]")
          }
        >
          {status.enabled ? (
            <>
              <Lock size={13} strokeWidth={1.7} />
              Disable
            </>
          ) : (
            <>
              <ShieldCheck size={13} strokeWidth={1.7} />
              Enable
            </>
          )}
        </button>
      </div>

      {/* Enrollment modal */}
      {phase === "verifying" && enrollment && (
        <Modal onClose={closeAll} title="Scan with your authenticator">
          <p className="mt-3 text-sm text-[#525252]">
            Use any TOTP app (1Password, Authy, Google Authenticator). The QR
            below holds your secret — type the 6-digit code it produces to
            confirm.
          </p>
          <div className="mt-5 flex justify-center">
            <img
              src={enrollment.qr_png_base64}
              alt="Scan with your authenticator"
              data-testid={`${testid}-qr`}
              className="rounded-2xl border hairline bg-white"
              width={220}
              height={220}
            />
          </div>
          <div className="mt-5 rounded-2xl bg-[#F2EFEA] p-3 break-all font-mono text-[11px] text-[#525252]">
            Can&rsquo;t scan? Paste: {enrollment.secret}
          </div>
          <label className="eyebrow mt-6 block">Six-digit code</label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            inputMode="numeric"
            data-testid={`${testid}-code`}
            className="orbit-input mt-2 font-mono text-lg tracking-[0.4em] text-center"
          />
          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              onClick={closeAll}
              data-testid={`${testid}-cancel`}
              className="btn-ghost !py-2 !px-5 !text-xs"
            >
              Not now
            </button>
            <button
              type="button"
              onClick={verifyEnroll}
              disabled={busy}
              data-testid={`${testid}-verify`}
              className="btn-primary !py-2 !px-5 !text-xs"
            >
              {busy ? "Verifying…" : "Verify"}
            </button>
          </div>
        </Modal>
      )}

      {/* Recovery codes */}
      {phase === "recovery" && (
        <Modal onClose={closeAll} title="Save these recovery codes">
          <p className="mt-3 text-sm text-[#525252]">
            Eight one-shot codes. Tuck them somewhere quiet — they let you sign in
            if you ever lose your authenticator.
          </p>
          <ul
            data-testid={`${testid}-recovery-list`}
            className="mt-5 grid grid-cols-2 gap-2 font-mono text-sm"
          >
            {recoveryCodes.map((c) => (
              <li
                key={c}
                className="rounded-xl bg-[#F2EFEA] px-3 py-2 tracking-wider"
              >
                {c}
              </li>
            ))}
          </ul>
          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              onClick={copyAll}
              data-testid={`${testid}-recovery-copy`}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-[#1F3A2A]/10 text-[#1F3A2A] text-xs tracking-[0.16em] uppercase font-medium hover:bg-[#1F3A2A]/20"
            >
              <Copy size={12} strokeWidth={1.7} />
              Copy all
            </button>
            <button
              type="button"
              onClick={closeAll}
              data-testid={`${testid}-recovery-done`}
              className="btn-primary !py-2 !px-5 !text-xs"
            >
              I&rsquo;ve saved them
            </button>
          </div>
        </Modal>
      )}

      {/* Disable */}
      {phase === "disabling" && (
        <Modal onClose={closeAll} title="Turn two-factor off">
          <p className="mt-3 text-sm text-[#525252]">
            Type a current six-digit code from your authenticator (or any
            unused recovery code) to confirm.
          </p>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            inputMode="numeric"
            data-testid={`${testid}-disable-code`}
            className="orbit-input mt-6 font-mono text-lg tracking-[0.4em] text-center"
          />
          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              onClick={closeAll}
              data-testid={`${testid}-disable-cancel`}
              className="btn-ghost !py-2 !px-5 !text-xs"
            >
              Keep it on
            </button>
            <button
              type="button"
              onClick={fullDisable}
              disabled={busy}
              data-testid={`${testid}-disable-confirm`}
              className="btn-primary !py-2 !px-5 !text-xs"
            >
              {busy ? "Turning off…" : "Turn off"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FBF9F6] rounded-3xl max-w-md w-full p-7 shadow-2xl max-h-[85vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <KeyRound size={14} strokeWidth={1.6} className="text-[#1F3A2A]" />
            <span className="eyebrow">Two-factor</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-full bg-white hover:bg-[#EAE5EF] flex items-center justify-center"
            aria-label="Close"
          >
            <X size={13} strokeWidth={1.6} />
          </button>
        </div>
        <h2 className="mt-2 font-serif text-2xl text-[#171717]">{title}</h2>
        {children}
      </div>
    </div>
  );
}
